// Handles EmptyState and ErrorState
import { useMemo } from 'react';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { auditHooks } from '../hooks';
import { type AuditEventListItem } from '../types';

interface ResourceAuditTrailProps {
 resourceType: string;
 resourceId: string;
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
 day: '2-digit',
 month: 'short',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 hour12: false,
});

export function ResourceAuditTrail({ resourceType, resourceId }: ResourceAuditTrailProps) {
 const defaultFilters = useMemo(() => {
 const toDate = new Date();
 const fromDate = new Date(toDate.getTime() - 90 * 24 * 60 * 60 * 1000);
 return {
 resource_type: resourceType,
 resourceId: resourceId,
 limit: 50,
 from: fromDate.toISOString(),
 to: toDate.toISOString(),
 };
 }, [resourceType, resourceId]);

 const { data: response, isLoading, error } = auditHooks.useList(defaultFilters);

  const config = defineListPage<AuditEventListItem>({
  variant: 'data-table',
  resource: 'items',
  columns: [
  { id: 'occurredAt', header: 'Timestamp', accessor: 'occurredAt', renderType: 'datetime' },
  { id: 'actorId', header: 'Actor', accessor: 'actorId', renderType: 'text' },
  { id: 'eventType', header: 'Event Type', accessor: 'eventType', renderType: 'text' },
  { id: 'summary', header: 'Summary', accessor: 'summary', renderType: 'text' },
  { id: 'ipAddress', header: 'IP Address', accessor: 'ipAddress', renderType: 'text' },
  ],
  });

  return (
  <div className="flex flex-col gap-4">
  <DataTable onRetry={() => window.location.reload()}
  config={config}
  data={response?.data || []}
  total={response?.meta?.total || 0}
  page={response?.meta?.page || 1}
  limit={response?.meta?.limit || 50}
  onPageChange={() => {}}
  loading={isLoading}
  error={error instanceof Error ? error.message : undefined}
      customRenderers={{
  occurredAt: (row: AuditEventListItem) => dateFormatter.format(new Date(row.occurredAt)),
  actorId: (row: AuditEventListItem) => {
  if (!row.actorId) return <span className="text-text-secondary">System</span>;
  return (
  <div className="flex flex-col">
  <span className="text-text-primary font-mono text-xs">{row.actorId}</span>
  <span className="text-caption text-text-tertiary uppercase">{row.actorAudience}</span>
  </div>
  );
  },
  eventType: (row: AuditEventListItem) => (
  <StatusBadge status="info" label={row.eventType} dot={false} className="font-mono text-caption" />
  ),
  ipAddress: (row: AuditEventListItem) => row.ipAddress || '-',
  }}
  />
  </div>
  );
}
